import type {
  TInvoiceCustomer,
  TSchoolSubscriptionData,
} from '@/types/subscription';

import { useMemo, useState, Activity, useEffect } from 'react';

import { IconClockDollar } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import EmptyState from '@/components/EmptyState';
import SubscriptionSkeleton from '@/components/SkeletonLoader/SubscriptionSkeleton';
import { ESubscriptionStatus } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import BillingHistoryTable from '@/pages/common/settingTabs/subscription/BillingHistoryTable';
import CancelSubscriptionCard from '@/pages/common/settingTabs/subscription/CancelSubscriptionCard';
import CurrentPlanCard from '@/pages/common/settingTabs/subscription/CurrentPlanCard';
import {
  getSubscription,
  cancelSubscription,
  requestSubscriptionRenewal,
} from '@/service/settingsService';
import useUser from '@/store/useUser';

const EMPTY_DATA: TSchoolSubscriptionData = {
  currentPlan: null,
  billingHistory: [],
};

const SchoolSubscription = () => {
  const { formatText } = useFormatText();
  const user = useUser((state) => state.user);

  const [data, setData] = useState<TSchoolSubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false);

  const customer: TInvoiceCustomer = useMemo(
    () => ({
      name: user.name,
      email: user.email,
    }),
    [user.email, user.name]
  );

  const fetchSubscription = async () => {
    try {
      setIsLoading(true);

      const response = await getSubscription();

      if (response?.status && response?.data) {
        setData(response.data);
      } else {
        setData(EMPTY_DATA);
      }
    } catch (error) {
      console.error(error);
      setData(EMPTY_DATA);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const handleConfirmCancel = async () => {
    try {
      setIsCancelling(true);

      const response = await cancelSubscription();

      if (response?.status) {
        setIsCancelModalOpen(false);
        fetchSubscription();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleConfirmRenew = async () => {
    try {
      setIsRenewing(true);

      const response = await requestSubscriptionRenewal();

      if (response?.status) {
        setIsRenewModalOpen(false);
        fetchSubscription();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRenewing(false);
    }
  };

  if (isLoading || !data) return <SubscriptionSkeleton />;

  const { currentPlan, billingHistory } = data;
  const subscriptionStatus = currentPlan?.status;

  const isExpiryPassed = currentPlan
    ? currentPlan.expiryDate * 1000 < Date.now()
    : false;
  const canCancel =
    !!currentPlan &&
    subscriptionStatus !== ESubscriptionStatus.CANCELLED &&
    subscriptionStatus !== ESubscriptionStatus.EXPIRED;
  const canRenew =
    subscriptionStatus === ESubscriptionStatus.EXPIRED ||
    subscriptionStatus === ESubscriptionStatus.EXPIRING_SOON ||
    (subscriptionStatus === ESubscriptionStatus.CANCELLED && isExpiryPassed);

  if (!currentPlan && billingHistory.length === 0) {
    return (
      <div className="max-w-216 w-full" data-testid="school-subscription">
        <EmptyState
          title={formatText('settings.subscription.no-active-plan-title')}
          description={formatText(
            'settings.subscription.no-active-plan-description'
          )}
          actions={
            <Button
              label={formatText('settings.subscription.renew-license')}
              icon={<IconClockDollar size={18} />}
              onClick={() => setIsRenewModalOpen(true)}
              loading={isRenewing}
            />
          }
        />
      </div>
    );
  }

  const renewButton = (
    <Button
      label={formatText('settings.subscription.renew-license')}
      icon={<IconClockDollar size={18} />}
      onClick={() => setIsRenewModalOpen(true)}
      loading={isRenewing}
      disabled={!canRenew}
    />
  );

  return (
    <>
      <div className="max-w-216 w-full" data-testid="school-subscription">
        <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
          {formatText('settings.subscription.billing-plan')}
        </h2>

        <CurrentPlanCard currentPlan={currentPlan} actions={renewButton} />

        <BillingHistoryTable
          billingHistory={billingHistory}
          customer={customer}
        />

        <CancelSubscriptionCard
          onCancelClick={() => setIsCancelModalOpen(true)}
          canCancel={canCancel}
          isCancelling={isCancelling}
        />
      </div>

      <Activity mode={isCancelModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isCancelModalOpen}
          setIsOpen={setIsCancelModalOpen}
          title="settings.subscription.cancel-confirm-title"
          descriptionPrefix="settings.subscription.cancel-confirm-description"
          descriptionSuffix="settings.subscription.cancel-confirm-description-suffix"
          confirmLabel={formatText('settings.subscription.confirm-cancel')}
          onConfirm={handleConfirmCancel}
          isLoading={isCancelling}
          isDestructive
          dataTestId="cancel-subscription-modal"
        />
      </Activity>

      <Activity mode={isRenewModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRenewModalOpen}
          setIsOpen={setIsRenewModalOpen}
          title="settings.subscription.renew-confirm-title"
          descriptionPrefix="settings.subscription.renew-confirm-description"
          descriptionSuffix="settings.subscription.renew-confirm-description-suffix"
          confirmLabel={formatText('settings.subscription.confirm-renew')}
          onConfirm={handleConfirmRenew}
          isLoading={isRenewing}
          dataTestId="renew-subscription-modal"
        />
      </Activity>
    </>
  );
};

export default SchoolSubscription;
